/**
 * Shared form validation helpers.
 * Use for input validation and on submit.
 */

// Name: letters, spaces, hyphens, apostrophes; no digits or special chars
const NAME_REGEX = /^[\p{L}\s\-']*$/u;
export function validateName(value, options = {}) {
  const { required = true, minLength = 1, maxLength = 200 } = options;
  const v = (value ?? "").trim();
  if (required && !v) return "Name is required.";
  if (v.length > maxLength) return `Name must be at most ${maxLength} characters.`;
  if (v.length > 0 && v.length < minLength) return `Name must be at least ${minLength} characters.`;
  if (v.length > 0 && !NAME_REGEX.test(v)) return "Name can only contain letters, spaces, hyphens and apostrophes.";
  return null;
}

// Email: valid email format
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function validateEmail(value, options = {}) {
  const { required = false } = options;
  const v = (value ?? "").trim();
  if (!v) return required ? "Email is required." : null;
  if (!EMAIL_REGEX.test(v)) return "Please enter a valid email address.";
  if (v.length > 254) return "Email is too long.";
  return null;
}

// Number: numeric only (optional decimals)
export function validateNumber(value, options = {}) {
  const { required = false, min, max, integer = false } = options;
  const v = (value ?? "").toString().trim();
  if (!v) return required ? "This field is required." : null;
  const n = parseFloat(v);
  if (Number.isNaN(n)) return "Please enter a valid number.";
  if (integer && !Number.isInteger(n)) return "Please enter a whole number.";
  if (min != null && n < min) return `Must be at least ${min}.`;
  if (max != null && n > max) return `Must be at most ${max}.`;
  return null;
}

// Restrict input to numeric (for onInput/onChange)
export function onlyNumericInput(e, allowDecimal = true) {
  const key = e.key;
  if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key)) return;
  if (allowDecimal && (key === "." || key === ",")) {
    const v = e.target.value;
    if (/[.,]/.test(v)) e.preventDefault();
    return;
  }
  if (!/^\d$/.test(key)) e.preventDefault();
}

// Restrict input for name (letters, space, hyphen, apostrophe)
export function onlyNameInput(e) {
  const key = e.key;
  if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key)) return;
  if (key.length === 1 && !NAME_REGEX.test(key)) e.preventDefault();
}

// Due date: must be today or in the future
export function validateDueDate(value) {
  const v = (value ?? "").trim();
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "Please enter a valid date.";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  if (d < today) return "Due date must be today or a future date.";
  return null;
}

// Date range: to_date must be >= from_date
export function validateDateRange(fromValue, toValue) {
  const from = (fromValue ?? "").trim();
  const to = (toValue ?? "").trim();
  if (!from || !to) return null;
  const dFrom = new Date(from);
  const dTo = new Date(to);
  if (Number.isNaN(dFrom.getTime())) return "From date is invalid.";
  if (Number.isNaN(dTo.getTime())) return "To date is invalid.";
  dFrom.setHours(0, 0, 0, 0);
  dTo.setHours(0, 0, 0, 0);
  if (dTo < dFrom) return "To date must be on or after from date.";
  return null;
}

// Journey date: valid date (optional future/today check via option)
export function validateJourneyDate(value, options = {}) {
  const { mustBeTodayOrFuture = false } = options;
  const v = (value ?? "").trim();
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "Please enter a valid date.";
  if (mustBeTodayOrFuture) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    if (d < today) return "Date must be today or a future date.";
  }
  return null;
}

// Phone: basic format (digits, spaces, +, -, parentheses)
const PHONE_REGEX = /^[\d\s+\-()]*$/;
export function validatePhone(value, options = {}) {
  const { required = false, maxLength = 30 } = options;
  const v = (value ?? "").trim();
  if (!v) return required ? "Phone is required." : null;
  if (!PHONE_REGEX.test(v)) return "Please enter a valid phone number.";
  if (v.length > maxLength) return `Phone must be at most ${maxLength} characters.`;
  return null;
}
